import React from 'react';
import {
  Box,
  Paper,
  IconButton,
  Divider,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import { YouTube } from './YouTubeExtension';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  Code,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  Title,
  RestartAlt,
  Image as ImageIcon,
  PlayCircle,
} from '@mui/icons-material';
import { RichTextControlProps } from './types';
import { LabelWithInfo } from './LabelWithInfo';
import { BaseButton } from '../BaseButton';
import mediaService from '../../../../services/MediaService';

/**
 * RichText control component using TipTap
 * Renders a rich text editor with toolbar and stores the JSON content
 */

// Memoized ToolbarButton component
interface MemoizedToolbarButtonProps {
  icon: React.ReactNode;
  tooltip: string;
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
}

const MemoizedToolbarButton = React.memo<MemoizedToolbarButtonProps>(
  ({ icon, tooltip, onClick, isActive, disabled }) => (
    <Tooltip title={tooltip}>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        sx={{
          color: isActive ? '#1976d2' : 'inherit',
          backgroundColor: isActive ? '#e3f2fd' : 'transparent',
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  )
);

MemoizedToolbarButton.displayName = 'MemoizedToolbarButton';

// Memoized Toolbar component
interface RichTextToolbarProps {
  editor: any;
  uploading: boolean;
  onImageClick: () => void;
  onYouTubeClick: () => void;
  onBoldClick: () => void;
  onItalicClick: () => void;
  onUnderlineClick: () => void;
  onStrikeClick: () => void;
  onH1Click: () => void;
  onH2Click: () => void;
  onH3Click: () => void;
  onBulletClick: () => void;
  onOrderedClick: () => void;
  onQuoteClick: () => void;
  onCodeBlockClick: () => void;
  onClearClick: () => void;
}

const RichTextToolbar = React.memo<RichTextToolbarProps>(({
  editor,
  uploading,
  onImageClick,
  onYouTubeClick,
  onBoldClick,
  onItalicClick,
  onUnderlineClick,
  onStrikeClick,
  onH1Click,
  onH2Click,
  onH3Click,
  onBulletClick,
  onOrderedClick,
  onQuoteClick,
  onCodeBlockClick,
  onClearClick,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 0.5,
      padding: 1,
      borderBottom: '1px solid #ccc',
      backgroundColor: '#f9f9f9',
      alignItems: 'center',
    }}
  >
    {/* Text formatting */}
    <MemoizedToolbarButton
      icon={<FormatBold />}
      tooltip="Bold (Ctrl+B)"
      onClick={onBoldClick}
      isActive={editor.isActive('bold')}
    />
    <MemoizedToolbarButton
      icon={<FormatItalic />}
      tooltip="Italic (Ctrl+I)"
      onClick={onItalicClick}
      isActive={editor.isActive('italic')}
    />
    <MemoizedToolbarButton
      icon={<FormatUnderlined />}
      tooltip="Underline"
      onClick={onUnderlineClick}
      isActive={editor.isActive('underline')}
    />
    <MemoizedToolbarButton
      icon={<StrikethroughS />}
      tooltip="Strikethrough"
      onClick={onStrikeClick}
      isActive={editor.isActive('strike')}
    />

    <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />

    {/* Headings */}
    <MemoizedToolbarButton
      icon={<Title sx={{ fontSize: 24 }} />}
      tooltip="Heading 1"
      onClick={onH1Click}
      isActive={editor.isActive('heading', { level: 1 })}
    />
    <MemoizedToolbarButton
      icon={<Title sx={{ fontSize: 20 }} />}
      tooltip="Heading 2"
      onClick={onH2Click}
      isActive={editor.isActive('heading', { level: 2 })}
    />
    <MemoizedToolbarButton
      icon={<Title sx={{ fontSize: 16 }} />}
      tooltip="Heading 3"
      onClick={onH3Click}
      isActive={editor.isActive('heading', { level: 3 })}
    />

    <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />

    {/* Lists & Quotes */}
    <MemoizedToolbarButton
      icon={<FormatListBulleted />}
      tooltip="Bullet List"
      onClick={onBulletClick}
      isActive={editor.isActive('bulletList')}
    />
    <MemoizedToolbarButton
      icon={<FormatListNumbered />}
      tooltip="Ordered List"
      onClick={onOrderedClick}
      isActive={editor.isActive('orderedList')}
    />
    <MemoizedToolbarButton
      icon={<FormatQuote />}
      tooltip="Blockquote"
      onClick={onQuoteClick}
      isActive={editor.isActive('blockquote')}
    />
    <MemoizedToolbarButton
      icon={<Code />}
      tooltip="Code Block"
      onClick={onCodeBlockClick}
      isActive={editor.isActive('codeBlock')}
    />

    <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />

    {/* Image */}
    <Tooltip title="Insert Image">
      <IconButton
        size="small"
        onClick={onImageClick}
        disabled={uploading}
        sx={{
          color: uploading ? '#ccc' : 'inherit',
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
        }}
      >
        {uploading ? <CircularProgress size={20} /> : <ImageIcon />}
      </IconButton>
    </Tooltip>

    {/* YouTube Video */}
    <Tooltip title="Insert YouTube Video">
      <IconButton
        size="small"
        onClick={onYouTubeClick}
        sx={{
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
        }}
      >
        <PlayCircle sx={{ color: '#FF0000' }} />
      </IconButton>
    </Tooltip>

    <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />

    {/* Clear formatting */}
    <MemoizedToolbarButton
      icon={<RestartAlt />}
      tooltip="Clear Formatting"
      onClick={onClearClick}
    />
  </Box>
));

RichTextToolbar.displayName = 'RichTextToolbar';

export const RichTextControl: React.FC<RichTextControlProps> = ({
  value,
  onChange,
  label,
  description,
  required,
  xControl,
}) => {
  debugger
  // Refs for debounce logic
  const debounceTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingContentRef = React.useRef<any>(null);
  const imageInputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = React.useState(false);
  const [youtubeDialogOpen, setYoutubeDialogOpen] = React.useState(false);
  const [youtubeUrl, setYoutubeUrl] = React.useState('');
  const DEBOUNCE_DELAY = 500; // 500ms debounce

  // Memoize editor config
  const editorConfig = React.useMemo(() => ({
    extensions: [StarterKit, Underline, Image, YouTube],
    content: value || { type: 'doc', content: [] },
  }), [value]);

  const editor = useEditor({
    ...editorConfig,
    onUpdate: ({ editor }) => {
      // Get the JSON content from the editor
      const jsonContent = editor.getJSON();
      pendingContentRef.current = jsonContent;

      // Clear existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set new timeout to update parent
      debounceTimeoutRef.current = setTimeout(() => {
        onChange(pendingContentRef.current);
      }, DEBOUNCE_DELAY);
    },
  });

  // Cleanup debounce on unmount or when content is reset
  React.useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Handle external value changes
  React.useEffect(() => {
    if (editor && value && JSON.stringify(editor.getJSON()) !== JSON.stringify(value)) {
      // Clear pending changes when external value updates
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      pendingContentRef.current = null;
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  // Handle image upload from paste or button
  const handleImageUpload = React.useCallback(async (file: File) => {
    if (!editor) return;

    setUploading(true);
    try {
      const response = await mediaService.uploadFile(file, true, [], '','blog');
      const imageUrl = (response as any)?.url || (response as any)?.data?.url || '';

      if (imageUrl) {
        editor
          .chain()
          .focus()
          .insertContent({
            type: 'image',
            attrs: {
              src: imageUrl,
            },
          })
          .run();
      }
    } catch (err) {
      console.error('Image upload failed:', err);
    } finally {
      setUploading(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  }, [editor]);

  // Handle image button click
  const handleImageButtonClick = React.useCallback(() => {
    imageInputRef.current?.click();
  }, []);

  // Handle image file input change
  const handleImageInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleImageUpload(file);
    }
  }, [handleImageUpload]);

  // Extract video ID from YouTube URL
  const getYouTubeVideoId = React.useCallback((url: string): string | null => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  }, []);

  // Handle YouTube video insertion
  const handleInsertYouTube = React.useCallback(() => {
    if (!youtubeUrl.trim()) return;

    const videoId = getYouTubeVideoId(youtubeUrl);
    if (!videoId) {
      alert('Invalid YouTube URL');
      return;
    }

    if (editor) {
      const embedUrl = `https://www.youtube.com/embed/${videoId}`;
      
      // Use the custom YouTube extension command
      (editor.chain() as any)
        .focus()
        .setYoutubeVideo({ src: embedUrl })
        .run();
    }

    setYoutubeUrl('');
    setYoutubeDialogOpen(false);
  }, [youtubeUrl, getYouTubeVideoId, editor]);

  // Memoized toolbar callbacks
  const onBoldClick = React.useCallback(() => editor?.chain().focus().toggleBold().run(), [editor]);
  const onItalicClick = React.useCallback(() => editor?.chain().focus().toggleItalic().run(), [editor]);
  const onUnderlineClick = React.useCallback(() => editor?.chain().focus().toggleUnderline().run(), [editor]);
  const onStrikeClick = React.useCallback(() => editor?.chain().focus().toggleStrike().run(), [editor]);
  const onH1Click = React.useCallback(() => editor?.chain().focus().toggleHeading({ level: 1 }).run(), [editor]);
  const onH2Click = React.useCallback(() => editor?.chain().focus().toggleHeading({ level: 2 }).run(), [editor]);
  const onH3Click = React.useCallback(() => editor?.chain().focus().toggleHeading({ level: 3 }).run(), [editor]);
  const onBulletClick = React.useCallback(() => editor?.chain().focus().toggleBulletList().run(), [editor]);
  const onOrderedClick = React.useCallback(() => editor?.chain().focus().toggleOrderedList().run(), [editor]);
  const onQuoteClick = React.useCallback(() => editor?.chain().focus().toggleBlockquote().run(), [editor]);
  const onCodeBlockClick = React.useCallback(() => editor?.chain().focus().toggleCodeBlock().run(), [editor]);
  const onClearClick = React.useCallback(() => editor?.chain().focus().clearNodes().run(), [editor]);

  // Handle paste event for images
  React.useEffect(() => {
    if (!editor) return;

    const handlePaste = (event: Event) => {
      const clipboardEvent = event as ClipboardEvent;
      const files = clipboardEvent.clipboardData?.files;
      if (!files) return;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type.startsWith('image/')) {
          clipboardEvent.preventDefault();
          handleImageUpload(file);
        }
      }
    };

    const editorElement = document.querySelector('.tiptap-editor .ProseMirror') as HTMLElement;
    if (editorElement) {
      (editorElement as any).addEventListener('paste', handlePaste);
      return () => {
        (editorElement as any).removeEventListener('paste', handlePaste);
      };
    }
  }, [editor]);

  if (!editor) {
    return null;
  }

  const minHeight = xControl?.minHeight || 300;
  const placeholder = xControl?.placeholder || 'Start typing...';

  return (
    <Box sx={{ mb: 2 }}>
      <LabelWithInfo label={label} description={description} required={required} />

      <Paper
        variant="outlined"
        sx={{
          border: '1px solid #ccc',
          borderRadius: 1,
          overflow: 'hidden',
          mt: 1,
        }}
      >
        {/* Toolbar */}
        <RichTextToolbar
          editor={editor}
          uploading={uploading}
          onImageClick={handleImageButtonClick}
          onYouTubeClick={() => setYoutubeDialogOpen(true)}
          onBoldClick={onBoldClick}
          onItalicClick={onItalicClick}
          onUnderlineClick={onUnderlineClick}
          onStrikeClick={onStrikeClick}
          onH1Click={onH1Click}
          onH2Click={onH2Click}
          onH3Click={onH3Click}
          onBulletClick={onBulletClick}
          onOrderedClick={onOrderedClick}
          onQuoteClick={onQuoteClick}
          onCodeBlockClick={onCodeBlockClick}
          onClearClick={onClearClick}
        />

        {/* Editor */}
        <Box
          className="tiptap-editor"
          sx={{
            minHeight: `${minHeight}px`,
            padding: 2,
            '& .ProseMirror': {
              outline: 'none',
              minHeight: `${minHeight}px`,
            },
            '& .ProseMirror p.is-editor-empty:first-child::before': {
              color: '#adb5bd',
              content: `attr(data-placeholder)`,
              float: 'left',
              height: 0,
              pointerEvents: 'none',
            },
            '& .ProseMirror h1': {
              fontSize: '2em',
              fontWeight: 'bold',
              margin: '0.67em 0',
            },
            '& .ProseMirror h2': {
              fontSize: '1.5em',
              fontWeight: 'bold',
              margin: '0.75em 0',
            },
            '& .ProseMirror h3': {
              fontSize: '1.17em',
              fontWeight: 'bold',
              margin: '0.83em 0',
            },
            '& .ProseMirror ul, & .ProseMirror ol': {
              paddingLeft: 24,
            },
            '& .ProseMirror li': {
              marginBottom: 4,
            },
            '& .ProseMirror pre': {
              backgroundColor: '#f5f5f5',
              color: '#333',
              fontFamily: 'monospace',
              padding: 12,
              borderRadius: 4,
              overflow: 'auto',
            },
            '& .ProseMirror code': {
              backgroundColor: '#f5f5f5',
              color: '#e83e8c',
              padding: '2px 4px',
              borderRadius: 2,
              fontFamily: 'monospace',
            },
            '& .ProseMirror blockquote': {
              borderLeft: '3px solid #ccc',
              color: '#666',
              margin: '16px 0',
              paddingLeft: 12,
            },
            '& .ProseMirror img': {
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 4,
              margin: '8px 0',
            },
            '& .youtube-container': {
              margin: '16px 0',
              display: 'flex',
              justifyContent: 'center',
            },
            '& .youtube-container iframe': {
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            },
          }}
        >
          {editor && <EditorContent editor={editor} data-placeholder={placeholder} />}
        </Box>

        {/* Hidden image input */}
        <input
          ref={imageInputRef}
          type="file"
          hidden
          accept="image/*"
          onChange={handleImageInputChange}
        />
      </Paper>

      {/* YouTube URL Dialog */}
      <Dialog open={youtubeDialogOpen} onClose={() => setYoutubeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Insert YouTube Video</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="YouTube URL"
            placeholder="https://www.youtube.com/watch?v=..."
            value={youtubeUrl}
            onChange={(e) => setYoutubeUrl(e.target.value)}
            helperText="Paste a YouTube video URL"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleInsertYouTube();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <BaseButton
            onClick={() => {
              setYoutubeDialogOpen(false);
              setYoutubeUrl('');
            }}
            variant="outlined"
            colorVariant="secondary"
          >
            Cancel
          </BaseButton>
          <BaseButton onClick={handleInsertYouTube} variant="contained" colorVariant="primary">
            Insert
          </BaseButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(RichTextControl);
